import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Paper,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  ExpandMore,
  MenuBook,
  School,
  CheckCircle,
  RadioButtonUnchecked,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";

interface VocabularyWord {
  word: string;
  meaning: string;
}

interface ReadingComprehensionQuestion {
  id: string;
  type: string;
  question: string;
  passage: string;
  vocabularyWords?: VocabularyWord[];
  options: string[];
  correctAnswer: number;
  points?: number;
  [key: string]: any;
}

interface ReadingComprehensionQuestionProps {
  question: ReadingComprehensionQuestion;
  userAnswer?: any;
  onAnswerChange: (answer: any) => void;
  onComplete?: (isCorrect: boolean, timeSpent: number) => void;
  disabled?: boolean;
  reviewMode?: boolean;
}

const ReadingComprehensionQuestionComponent: React.FC<
  ReadingComprehensionQuestionProps
> = ({
  question,
  userAnswer,
  onAnswerChange,
  onComplete,
  disabled = false,
  reviewMode = false,
}) => {
  const theme = useTheme();
  const startTimeRef = useRef(Date.now());
  const hasCompletedRef = useRef(false);

  // State
  const [vocabularyExpanded, setVocabularyExpanded] = useState(false);

  const selectedAnswer = useMemo(() => {
    if (userAnswer === undefined || userAnswer === null || userAnswer === "") {
      return "";
    }
    if (typeof userAnswer === "string") {
      return userAnswer;
    }
    if (typeof userAnswer === "number" && question.options[userAnswer]) {
      return question.options[userAnswer];
    }
    return "";
  }, [userAnswer, question.options]);

  // Handle answer selection
  const handleAnswerSelect = useCallback(
    (value: string) => {
      if (disabled || reviewMode) return;

      const selectedIndex = question.options.indexOf(value);
      onAnswerChange(value); // Parent manages state

      if (onComplete && !hasCompletedRef.current) {
        hasCompletedRef.current = true;
        const timeSpent = Date.now() - startTimeRef.current;
        const isCorrect = selectedIndex === question.correctAnswer;
        onComplete(isCorrect, timeSpent);
      }
    },
    [
      disabled,
      reviewMode,
      question.options,
      question.correctAnswer,
      onAnswerChange,
      onComplete,
    ]
  );

  const isAnswered = !!selectedAnswer;
  const correctAnswerText = question.options[question.correctAnswer];

  return (
    <Box>
      {/* Question */}
      <Typography variant="h6" gutterBottom>
        {question.question}
      </Typography>

      {/* Passage */}
      <Card sx={{ mb: 3, backgroundColor: theme.palette.grey[50] }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <MenuBook color="primary" />
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              Reading Passage
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: "background.paper",
              border: `1px solid ${theme.palette.divider}`,
              maxHeight: 400,
              overflowY: "auto",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                textAlign: "justify",
                whiteSpace: "pre-wrap",
              }}
            >
              {question.passage}
            </Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* Vocabulary Helper (if provided) */}
      {question.vocabularyWords && question.vocabularyWords.length > 0 && (
        <Accordion
          expanded={vocabularyExpanded}
          onChange={() => setVocabularyExpanded(!vocabularyExpanded)}
          sx={{ mb: 3 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <School color="action" />
              <Typography variant="subtitle1">Vocabulary Helper</Typography>
              <Chip
                label={`${question.vocabularyWords.length} words`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {question.vocabularyWords.map((vocab, index) => (
                <Box key={index}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {vocab.word}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {vocab.meaning}
                    </Typography>
                  </Box>
                  {index < question.vocabularyWords!.length - 1 && (
                    <Divider sx={{ my: 1 }} />
                  )}
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Answer Options */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          border: isAnswered
            ? `1px solid ${theme.palette.success.light}`
            : "none",
          backgroundColor: isAnswered
            ? theme.palette.success.light + "10"
            : "background.paper",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          {isAnswered ? (
            <CheckCircle color="success" fontSize="small" />
          ) : (
            <RadioButtonUnchecked color="disabled" fontSize="small" />
          )}
          <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
            Select the best answer:
          </Typography>
        </Box>

        <RadioGroup
          value={selectedAnswer}
          onChange={(e) => handleAnswerSelect(e.target.value)}
        >
          {question.options.map((option, index) => (
            <FormControlLabel
              key={index}
              value={option}
              control={<Radio disabled={disabled || reviewMode} />}
              label={
                <Typography variant="body1">
                  {String.fromCharCode(65 + index)}. {option}
                </Typography>
              }
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor:
                  reviewMode && option === correctAnswerText
                    ? theme.palette.success.light + "40"
                    : selectedAnswer === option
                    ? theme.palette.primary.light + "20"
                    : "transparent",
                "&:hover":
                  !disabled && !reviewMode
                    ? { backgroundColor: theme.palette.action.hover }
                    : {},
                transition: "all 0.2s ease",
              }}
            />
          ))}
        </RadioGroup>

        {/* Review Mode - Show Correct Answer */}
        {reviewMode &&
          selectedAnswer &&
          selectedAnswer !== correctAnswerText && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Correct answer:</strong>{" "}
                {String.fromCharCode(65 + question.correctAnswer)}.{" "}
                {correctAnswerText}
              </Typography>
            </Alert>
          )}

        {/* Review Mode - Show Success */}
        {reviewMode && selectedAnswer === correctAnswerText && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="body2">Correct! Well done.</Typography>
          </Alert>
        )}
      </Paper>

      {/* Points Display */}
      {question.points && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
          <Chip
            label={`${question.points} points`}
            size="small"
            color="secondary"
            variant="outlined"
          />
        </Box>
      )}
    </Box>
  );
};

export default ReadingComprehensionQuestionComponent;
